/*
 * CEOChallengeStart — Start screen for the Youth CEO Camp Final Challenge.
 * Collects student name (prefilled) + optional team name, then kicks off
 * an attempt via POST /api/ceo-challenge/start.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Sparkles, Timer, Trophy, ArrowRight, Users } from "lucide-react";
import { LemonBot, THEME } from "./ceo_ui";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CEOChallengeStart({ user }) {
  const nav = useNavigate();
  const [config, setConfig] = useState(null);
  const [studentName, setStudentName] = useState(user?.name || "");
  const [teamName, setTeamName] = useState("");
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);
  const [remaining, setRemaining] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [cfg, attempts] = await Promise.all([
          axios.get(`${API}/ceo-challenge/config`),
          axios.get(`${API}/ceo-challenge/my-attempts`),
        ]);
        if (!alive) return;
        setConfig(cfg.data);
        const completed = (attempts.data.attempts || []).filter((a) => a.status === "completed").length;
        setRemaining(Math.max(0, (cfg.data.max_attempts || 3) - completed));
      } catch (err) {
        console.error(err);
        setError("Could not load quiz config. Please refresh.");
      }
    })();
    return () => { alive = false; };
  }, []);

  const handleStart = async () => {
    if (!studentName.trim()) {
      setError("Please enter your name.");
      return;
    }
    setStarting(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/ceo-challenge/start`, {
        student_name: studentName.trim(),
        team_name: teamName.trim() || null,
      });
      // Stash the started attempt so refreshes don't lose state.
      sessionStorage.setItem("ceo_current_attempt", JSON.stringify(res.data));
      nav(`/ceo-challenge/play`);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not start. Please try again.");
      setStarting(false);
    }
  };

  return (
    <div className={`min-h-screen ${THEME.bg} text-white relative overflow-hidden`}>
      {/* Ambient background */}
      <div className="absolute inset-0 opacity-30" aria-hidden="true">
        <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-[#7C3AED] blur-3xl" />
        <div className="absolute top-1/3 -right-32 w-96 h-96 rounded-full bg-[#38BDF8] blur-3xl" />
        <div className="absolute bottom-0 left-1/2 w-96 h-96 rounded-full bg-[#FFD500] blur-3xl -translate-x-1/2" />
      </div>

      <div className="relative max-w-3xl mx-auto px-5 py-12 sm:py-16">
        <div className="flex flex-col items-center text-center mb-8">
          <LemonBot size={110} mood="cheer" data-testid="ceo-start-lemonbot" />
          <p className="mt-6 text-xs font-black tracking-[0.28em] uppercase text-[#FFD500]" data-testid="ceo-start-eyebrow">
            Youth CEO Camp
          </p>
          <h1
            className="mt-2 text-[38px] sm:text-5xl lg:text-[64px] font-black tracking-tight leading-[0.98]"
            data-testid="ceo-start-title"
          >
            Final Challenge
          </h1>
          <p className="mt-3 text-lg sm:text-xl text-white/80">
            {config?.subtitle || "Are You Ready for Pitch Day?"}
          </p>
          <p className="mt-5 max-w-xl text-base sm:text-lg text-white/70">
            Test what you learned about entrepreneurship, customers, branding, marketing,
            business basics, and pitching.
          </p>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-8">
          <StatCard
            icon={<Sparkles className="w-5 h-5" />}
            label="Questions"
            value={config?.total_questions ?? 20}
          />
          <StatCard
            icon={<Timer className="w-5 h-5" />}
            label="Est. Time"
            value="8–12 min"
          />
          <StatCard
            icon={<Trophy className="w-5 h-5" />}
            label="Passing Score"
            value={`${config?.passing_score ?? 12} correct`}
          />
        </div>

        {/* Form card */}
        <div className="bg-white/5 backdrop-blur rounded-3xl ring-1 ring-white/10 p-6 sm:p-8 shadow-2xl">
          <label className="block text-sm font-bold text-white/80 mb-2" htmlFor="ceo-name">
            Your name
          </label>
          <input
            id="ceo-name"
            data-testid="ceo-start-name-input"
            value={studentName}
            onChange={(e) => setStudentName(e.target.value)}
            placeholder="Type your first + last name"
            className="w-full bg-[#0B1E3F] text-white text-lg rounded-xl px-4 py-3.5 ring-2 ring-white/10 focus:ring-[#FFD500] focus:outline-none placeholder-white/30"
            maxLength={80}
            autoFocus
          />

          <label className="block text-sm font-bold text-white/80 mb-2 mt-5" htmlFor="ceo-team">
            Team name <span className="text-white/40 font-normal">(optional)</span>
          </label>
          <div className="relative">
            <Users className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              id="ceo-team"
              data-testid="ceo-start-team-input"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              placeholder="e.g. Zesty Ideas"
              className="w-full bg-[#0B1E3F] text-white text-lg rounded-xl pl-11 pr-4 py-3.5 ring-2 ring-white/10 focus:ring-[#FFD500] focus:outline-none placeholder-white/30"
              maxLength={80}
            />
          </div>

          {error && (
            <p className="mt-4 text-sm font-semibold text-[#FCA5A5]" data-testid="ceo-start-error">
              {error}
            </p>
          )}

          {typeof remaining === "number" && remaining < (config?.max_attempts ?? 3) && (
            <p className="mt-4 text-xs text-white/60" data-testid="ceo-start-attempts-hint">
              You have {remaining} attempt{remaining === 1 ? "" : "s"} remaining.
            </p>
          )}

          <button
            data-testid="ceo-start-button"
            onClick={handleStart}
            disabled={starting || remaining === 0}
            className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#FFD500] text-black text-lg font-black rounded-full py-4 shadow-[0_10px_30px_rgba(255,213,0,0.35)] hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {starting ? "Starting…" : remaining === 0 ? "No attempts remaining" : "Start the CEO Challenge"}
            {!starting && remaining !== 0 && <ArrowRight className="w-5 h-5" />}
          </button>

          <p className="mt-4 text-center text-xs text-white/50">
            Timer per question: {config?.time_per_question ?? 20}s • Speed bonus up to {config?.max_speed_bonus ?? 50} pts
          </p>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value }) {
  return (
    <div className="bg-white/5 backdrop-blur rounded-2xl ring-1 ring-white/10 p-3 sm:p-4 text-center">
      <div className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-[#FFD500]/15 text-[#FFD500] mb-2">
        {icon}
      </div>
      <p className="text-[11px] font-black uppercase tracking-widest text-white/60">{label}</p>
      <p className="mt-1 text-lg sm:text-xl font-black">{value}</p>
    </div>
  );
}
